const DEFAULT_API_BASE =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

function appendRecommendQuery(url, { tenantId, userId, personaId, accountId, isTemporary }) {
  url.searchParams.set('tenant_id', tenantId);
  if (userId) url.searchParams.set('user_id', userId);
  if (personaId) {
    url.searchParams.set('persona_id', String(personaId));
    url.searchParams.set('person_id', String(personaId));
  }
  if (accountId) url.searchParams.set('account_id', String(accountId));
  if (isTemporary) url.searchParams.set('is_temporary', 'true');
}

async function fetchRecommendUrl(url, signal) {
  const res = await fetch(url.toString(), { credentials: 'include', signal });
  if (res.status === 422 || res.status === 404) return null;
  if (!res.ok) {
    if (res.status >= 500) {
      console.error('[recommended-send-time] request failed', res.status, url.pathname);
    }
    return null;
  }
  return res.json();
}

async function fetchRecommendForStepPath(apiBase, stepId, query, signal) {
  const url = new URL(
    `${apiBase}/api/outbox/${encodeURIComponent(stepId)}/recommended-send-time`,
  );
  appendRecommendQuery(url, query);
  return fetchRecommendUrl(url, signal);
}

/**
 * GET recommended send time — tries step-scoped route, then ad-hoc collection route.
 * @returns {Promise<object|null>} payload on 200; null on 422/404/other (500 logged)
 */
export async function fetchRecommendedSendTime({
  stepId,
  tenantId,
  userId,
  personaId,
  accountId,
  isTemporary = false,
  apiBase = DEFAULT_API_BASE,
  signal,
}) {
  if (!tenantId) return null;

  const query = { tenantId, userId, personaId, accountId, isTemporary };
  const stepCandidates = [];

  if (stepId) stepCandidates.push(String(stepId));
  if (isTemporary && personaId) {
    stepCandidates.push('adhoc', 'ad-hoc', 'new');
  }

  const seen = new Set();
  for (const candidate of stepCandidates) {
    if (!candidate || seen.has(candidate)) continue;
    seen.add(candidate);
    const data = await fetchRecommendForStepPath(apiBase, candidate, query, signal);
    if (data?.recommended_send_at) return data;
  }

  if (isTemporary && personaId) {
    const adHocUrl = new URL(`${apiBase}/api/outbox/ad-hoc/recommended-send-time`);
    appendRecommendQuery(adHocUrl, query);
    const data = await fetchRecommendUrl(adHocUrl, signal);
    if (data?.recommended_send_at) return data;
  }

  return null;
}

/** Prefer server-issued outbox step id from generate-message when present. */
export function extractOutboxStepIdFromGenerateMessage(data) {
  if (!data || typeof data !== 'object') return null;
  const msg = data.message && typeof data.message === 'object' ? data.message : null;
  const first = Array.isArray(data.messages) ? data.messages[0] : null;
  const candidates = [
    data.step_id,
    data.stepId,
    data.outbox_step_id,
    data.outboxStepId,
    data.draft_id,
    data.draftId,
    data.id,
    msg?.step_id,
    msg?.stepId,
    msg?.id,
    first?.step_id,
    first?.stepId,
    first?.id,
    data.draft?.id,
    data.draft?.step_id,
  ];
  for (const c of candidates) {
    const s = String(c ?? '').trim();
    if (!s || s.startsWith('draft-')) continue;
    return s;
  }
  return null;
}

/** Map ISO instant to value for <input type="datetime-local" /> in the browser's local zone. */
export function isoToDatetimeLocalValue(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function isAdHocTemporaryOutboxDraft({ stepId, isTemporary }) {
  return Boolean(isTemporary) || String(stepId || '').startsWith('draft-');
}

export function isOutboxStepEligibleForRecommendedSendTime({
  stepId,
  isSent,
  isHistoryItem,
  channel,
}) {
  if (!stepId || isSent || isHistoryItem) return false;
  const ch = String(channel || 'email').toLowerCase();
  if (ch.startsWith('linkedin')) return false;
  return ch === 'email' || ch === 'e-mail' || ch === '';
}
